import { LitElement, html } from "lit-element";
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { lineWeight } from "../styles/icons";
import './barcode-scanner';

class BarcodeBox extends LitElement {

	static get properties() {
		return {
			value: { type: String },
			name: { type: String },
			label: { type: String },
			required: { type: Boolean },
			disabled: { type: Boolean },
			path: { type: String },
			helperText: { type: String },
			pattern: { type: String },
			validationMessage: { type: String },
			min: { type: String },
			max: { type: String },
			isInvalid: { type: Boolean },
		};
	}

	render() {
		const value = this.value != undefined ? this.value : "";
		return html`
        <style>
            * {
                box-sizing: border-box;
            }
        
            :host {
                display: block;
                box-sizing: border-box;
            }
        
            .group {
				margin-bottom: 10px;
                padding-top: 16px;
                position: relative;
                background-color: var(--primary-color-variant3);
                border-radius: 3px 3px 0px 0px;
            }
        
            input {
                background: none;
                color: var(--secondary-color-variant1);
                font-size: 18px;
                padding: 10px 34px 10px 5px;
                display: block;
                width: 100%;
                border: none;
                border-radius: 0;
                border-bottom: 1px solid var(--secondary-color-variant1);
			    box-shadow: none;
            }
        
            input:focus {
                outline: none;
                color: var(--secondary-color);
            }

			/* active state */
            input:focus~label,
            input[has-value]~label {
                top: 4px;
                font-size: 12px;
                color: var(--secondary-color-variant1);
            }

			/* disabled state */
			input:disabled:not(:placeholder-shown) {
				color: #9F9EA0;
			}
			
			input:disabled:not(:placeholder-shown)~label {
				top: 4px;
                font-size: 12px;
				color: #9F9EA0;
			}

			input:disabled~.icon {
				cursor: not-allowed;
			}
        
            input:focus~.bar:before {
                width: 100%;
            }

			input[type="datetime-local"],
			input[type="date"],
			input[type="time"] {
                height: 42px;
            }
        
            input[type="password"] {
                letter-spacing: 0.3em;
            }
        
            label {
                color: #c6c6c6;
                font-size: 16px;
                font-weight: normal;
                position: absolute;
                pointer-events: none;
                left: 5px;
                top: 8px;
                transition: 300ms ease all;
                width: calc(100% - 5px);
                -webkit-line-clamp: 2;
                text-overflow: ellipsis;
                overflow: hidden;
                max-height: 50px;
            }

            input[type="datetime-local"]~label,
			input[type="date"]~label{
               text-overflow: ellipsis;
               overflow: hidden;
               max-height: 25px;
            }

            input:focus~label,
			input[has-value]~label {  
                width: 99%;       
                white-space: nowrap;
            }
        
            .bar {
                position: relative;
                display: block;
                width: auto;
            }
        
            .bar:before {
                content: '';
                height: 2px;
                width: 0;
                bottom: 0px;
                position: absolute;
                background: var(--secondary-color);
                transition: 300ms ease all;
                left: 0%;
            }

			.group > .helper-text {
				visibility: hidden;
				position: absolute;
				left: 5px;
				bottom: -16px;
				margin: 0;
				padding: 0;
				color: rgba(0, 0, 0, 0.6);
				font-size: 0.75rem;
				background-color: white;
				height: calc(1em + 4px);
				overflow: hidden;
				text-overflow: ellipsis;
				word-break: break-all;
				white-space: nowrap;
				width: 100%;
			}

			.group > .helper-text[isVisible] {
				visibility: visible;
			}
			
			input[invalid] ~ .helper-text,
			input[invalid] ~ label {
				color: red;                
			}

            input[invalid] {
                border-bottom: 1px solid red;
            }

			input[invalid] ~ .bar:before {
				background: red;
			}

			.icon {
				cursor: pointer;
				color: var(--secondary-color-variant1);
				fill: var(--secondary-color-variant1);
				width: 24px;
				height: 24px;
                position: absolute;
                right: 5px;
				top: 17px;
				transform: rotate(90deg);
			}

			#fileInput {
				display: none !important;
			}
        </style>
        
        <div class="group">
            ${this.renderInput(value)}
            <span class="highlight"></span>
            <span class="bar"></span>
			<label>${this.getLabelText()}</label>
			<p class="helper-text" ?isVisible="${this.isHelperTextVisible()}">
				${this.getHelperText()}
			</p>
			<span class="icon" @click="${(e) => this.scanClicked(e)}">${lineWeight}</span>
		</div>`;
	}

	getLabelText() {
		return this.label + (this.required ? '*' : '');
	}

	isHelperTextVisible() {
		return (this.isInvalid || this.helperText);
	}

	getHelperText() {
		return this.isInvalid ? (this.validationMessage || this._getDefaultMessage()) : this.helperText;
	}

	_hasValue(value) {
		return (value !== undefined && value !== null && value !== "");
	}

	renderInput(value) {
		const hasValue = this._hasValue(value);

		return html`<input type="text"
            .value="${value}"
            ?has-value="${hasValue}"
            @change="${(e) => this.handleChange(e)}"
            @blur="${(e) => this.handleBlur(e)}"
            ?required="${this.required}"
            ?disabled="${this.disabled}"
            ?invalid="${this.isInvalid}"
            min="${ifDefined(this.min)}"
            max="${ifDefined(this.max)}" />`;
	}

	_dispatchEvent(name, detail) {
		this.dispatchEvent(
			new CustomEvent(name, {
				detail: detail,
				composed: true
			})
		);
	}

	handleBlur(e) {
		if (!this.path) return;
		if (this._wasChangeEvent) {
			this._wasChangeEvent = false;
			return;
		}

		this._dispatchEvent("inspx-input-blur", { target: this, path: this.path });
	}

	handleChange(e) {
		this._wasChangeEvent = true;
		this.value = e.target.value;
		if (this.path) {
			const v = this.getAdjustedValue(e.target.value);
			this._dispatchEvent("inspx-change", { target: this, value: v, path: this.path });
		} else {
			this._dispatchEvent("change", { value: e.target.value });
		}
	}

	getAdjustedValue(v) {
		let result = v;
		if (this.required && result === "") {
			result = undefined;
		}
		return result;
	}

	_getDefaultMessage() {
		return "The '" + this.label + "' is invalid!";
	}

	scanClicked() {
		if (this.disabled) return;

		var v = document.createElement("barcode-scanner");
		v.addEventListener("barcode-scanned", (e) => {
			this.handleChange({ target: { value: e.detail } });
		});
		this.shadowRoot.appendChild(v);
	}
}

window.customElements.define("barcode-box", BarcodeBox);
